import {
  BONUS_CARD_DENOMINATIONS,
  BONUS_CARD_VARIANTS,
  getBonusCardVariant,
  type BonusCardDenomination,
  type BonusCardVariantId,
} from '../../../assets/cards';
import type { MedalOutcome } from '../../physics';
import type {
  BonusCardCollectionSnapshot,
  BonusCardDenominationCounts,
  BonusCardLastCollection,
  BonusCardMedalOutcomeHandler,
  BonusCardRewardAwardedEvent,
  BonusCardRewardConfiguration,
  BonusCardRewardEvent,
  BonusCardRewardEventListener,
  BonusCardRewardOptions,
  BonusCardRewardResetOptions,
  BonusCardRewardSerializedState,
  BonusCardRewardStateListener,
  BonusCardRewardTelemetry,
  BonusCardRewardUnsubscribe,
  BonusCardSetCompleteEvent,
  BonusCardVariantCounts,
  BonusOutcomeCollected,
  BonusOutcomeConsumption,
  BonusOutcomeIgnored,
  BonusOutcomeIgnoredReason,
  BonusOutcomeRejected,
} from './types';

export const BONUS_CARD_REWARD_LIMITS = Object.freeze({
  setSize: Object.freeze({ min: 1, max: 100 }),
  cardCoinAward: Object.freeze({ min: 0, max: 100 }),
  setCompletionCoinAward: Object.freeze({ min: 0, max: 500 }),
  jackpotContribution: Object.freeze({ min: 0, max: 1_000 }),
});

export const DEFAULT_BONUS_CARD_COIN_AWARDS: BonusCardDenominationCounts =
  Object.freeze({
    50: 1,
    100: 2,
    200: 4,
    500: 10,
    1000: 20,
  });

export const DEFAULT_BONUS_CARD_JACKPOT_CONTRIBUTIONS: BonusCardDenominationCounts =
  Object.freeze({
    50: 1,
    100: 2,
    200: 4,
    500: 10,
    1000: 20,
  });

export const DEFAULT_BONUS_CARD_SET_SIZE = 5;
export const DEFAULT_BONUS_CARD_SET_COIN_AWARD = 5;
export const DEFAULT_BONUS_CARD_SET_JACKPOT_CONTRIBUTION = 5;

const EMPTY_EVENTS: readonly BonusCardRewardEvent[] = Object.freeze([]);

/**
 * Idempotent collection and reward state for front-chute bonus cards.
 *
 * This class deliberately owns neither the playable coin balance nor jackpot
 * state. A host grants `reward.extraCoins`, while a separate jackpot system may
 * consume `reward.recommendedJackpotContribution`.
 */
export class BonusCardRewardSystem {
  private readonly configurationValue: BonusCardRewardConfiguration;
  private readonly eventListeners = new Set<BonusCardRewardEventListener>();
  private readonly stateListeners = new Set<BonusCardRewardStateListener>();
  private readonly onError: BonusCardRewardOptions['onError'];
  private readonly processedIds = new Set<string>();

  private variantCountsValue = createEmptyVariantCounts();
  private denominationCountsValue = createEmptyDenominationCounts();
  private totalCardsValue = 0;
  private completedSetsValue = 0;
  private totalCardRewardCoinsValue = 0;
  private totalSetCompletionRewardCoinsValue = 0;
  private lastCollectedValue: BonusCardLastCollection | null = null;
  private eventSequenceValue = 0;
  private disposedValue = false;

  private outcomesReceivedValue = 0;
  private cardWinsCollectedValue = 0;
  private ignoredCoinOutcomesValue = 0;
  private ignoredCardLossesValue = 0;
  private duplicateOutcomeIdsRejectedValue = 0;
  private eventsEmittedValue = 0;
  private cardCollectedEventsValue = 0;
  private setCompleteEventsValue = 0;
  private rewardEventsValue = 0;
  private resetsValue = 0;
  private restoresValue = 0;
  private lastOutcomeIdValue: string | null = null;
  private lastCollectedOutcomeIdValue: string | null = null;

  /** Safe to pass directly to MedalPhysicsSystem.onOutcome(). */
  readonly onMedalOutcome: BonusCardMedalOutcomeHandler = (outcome) => {
    this.consumeOutcome(outcome);
  };

  constructor(options: BonusCardRewardOptions = {}) {
    this.configurationValue = normalizeConfiguration(options);
    this.onError = options.onError;
    if (options.onEvent) this.eventListeners.add(options.onEvent);
    if (options.onStateChange) this.stateListeners.add(options.onStateChange);
  }

  get configuration(): BonusCardRewardConfiguration {
    return this.configurationValue;
  }

  get snapshot(): BonusCardCollectionSnapshot {
    return this.getSnapshot();
  }

  get telemetry(): BonusCardRewardTelemetry {
    return this.getTelemetry();
  }

  get disposed(): boolean {
    return this.disposedValue;
  }

  getSnapshot(): BonusCardCollectionSnapshot {
    const setProgress = this.totalCardsValue % this.configurationValue.setSize;
    return Object.freeze({
      schemaVersion: 1 as const,
      totalCards: this.totalCardsValue,
      variantCounts: freezeVariantCounts(this.variantCountsValue),
      denominationCounts: freezeDenominationCounts(this.denominationCountsValue),
      setSize: this.configurationValue.setSize,
      setProgress,
      cardsUntilSetComplete: this.configurationValue.setSize - setProgress,
      completedSets: this.completedSetsValue,
      totalCardRewardCoins: this.totalCardRewardCoinsValue,
      totalSetCompletionRewardCoins: this.totalSetCompletionRewardCoinsValue,
      totalRewardCoins: safeAdd(
        this.totalCardRewardCoinsValue,
        this.totalSetCompletionRewardCoinsValue,
        'totalRewardCoins',
      ),
      processedOutcomeIds: this.processedIds.size,
      lastCollected: this.lastCollectedValue,
    });
  }

  getTelemetry(): BonusCardRewardTelemetry {
    return Object.freeze({
      outcomesReceived: this.outcomesReceivedValue,
      uniqueOutcomesProcessed: this.processedIds.size,
      cardWinsCollected: this.cardWinsCollectedValue,
      ignoredCoinOutcomes: this.ignoredCoinOutcomesValue,
      ignoredCardLosses: this.ignoredCardLossesValue,
      duplicateOutcomeIdsRejected: this.duplicateOutcomeIdsRejectedValue,
      eventsEmitted: this.eventsEmittedValue,
      cardCollectedEvents: this.cardCollectedEventsValue,
      setCompleteEvents: this.setCompleteEventsValue,
      rewardEvents: this.rewardEventsValue,
      totalRewardCoins: safeAdd(
        this.totalCardRewardCoinsValue,
        this.totalSetCompletionRewardCoinsValue,
        'telemetry.totalRewardCoins',
      ),
      resets: this.resetsValue,
      restores: this.restoresValue,
      lastOutcomeId: this.lastOutcomeIdValue,
      lastCollectedOutcomeId: this.lastCollectedOutcomeIdValue,
    });
  }

  subscribe(listener: BonusCardRewardEventListener): BonusCardRewardUnsubscribe {
    this.assertActive();
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  subscribeState(
    listener: BonusCardRewardStateListener,
    emitImmediately = true,
  ): BonusCardRewardUnsubscribe {
    this.assertActive();
    this.stateListeners.add(listener);
    if (emitImmediately) this.invokeSafely(() => listener(this.getSnapshot()));
    return () => this.stateListeners.delete(listener);
  }

  hasProcessedOutcome(outcomeId: string): boolean {
    return this.processedIds.has(readOutcomeId(outcomeId));
  }

  /**
   * Processes a unique physics outcome once. Coin outcomes and card losses are
   * retained as processed IDs but do not affect collection or rewards.
   */
  consumeOutcome(outcome: MedalOutcome): BonusOutcomeConsumption {
    this.assertActive();
    this.outcomesReceivedValue = safeIncrement(
      this.outcomesReceivedValue,
      'telemetry.outcomesReceived',
    );

    const outcomeId = readOutcomeId(outcome.id);
    this.lastOutcomeIdValue = outcomeId;
    if (this.processedIds.has(outcomeId)) {
      this.duplicateOutcomeIdsRejectedValue = safeIncrement(
        this.duplicateOutcomeIdsRejectedValue,
        'telemetry.duplicateOutcomeIdsRejected',
      );
      const rejected: BonusOutcomeRejected = Object.freeze({
        status: 'rejected',
        processed: false,
        reason: 'duplicate-outcome-id',
        outcomeId,
        events: EMPTY_EVENTS,
        snapshot: this.getSnapshot(),
      });
      return rejected;
    }

    if (outcome.itemKind === 'coin') {
      this.processedIds.add(outcomeId);
      this.ignoredCoinOutcomesValue = safeIncrement(
        this.ignoredCoinOutcomesValue,
        'telemetry.ignoredCoinOutcomes',
      );
      return this.createIgnoredResult(outcomeId, 'coin-outcome');
    }

    if (outcome.result !== 'front-win') {
      this.processedIds.add(outcomeId);
      this.ignoredCardLossesValue = safeIncrement(
        this.ignoredCardLossesValue,
        'telemetry.ignoredCardLosses',
      );
      return this.createIgnoredResult(outcomeId, 'card-not-front-win');
    }

    return this.collectCard(outcome, outcomeId);
  }

  consumeOutcomes(outcomes: Iterable<MedalOutcome>): readonly BonusOutcomeConsumption[] {
    this.assertActive();
    const results: BonusOutcomeConsumption[] = [];
    for (const outcome of outcomes) results.push(this.consumeOutcome(outcome));
    return Object.freeze(results);
  }

  reset(options: BonusCardRewardResetOptions = {}): BonusCardCollectionSnapshot {
    this.assertActive();
    this.variantCountsValue = createEmptyVariantCounts();
    this.denominationCountsValue = createEmptyDenominationCounts();
    this.totalCardsValue = 0;
    this.completedSetsValue = 0;
    this.totalCardRewardCoinsValue = 0;
    this.totalSetCompletionRewardCoinsValue = 0;
    this.lastCollectedValue = null;
    this.lastCollectedOutcomeIdValue = null;
    if (options.clearProcessedOutcomeIds ?? true) this.processedIds.clear();

    if (options.clearTelemetry ?? true) {
      this.clearTelemetry();
      this.resetsValue = 1;
    } else {
      this.resetsValue = safeIncrement(this.resetsValue, 'telemetry.resets');
    }

    const snapshot = this.getSnapshot();
    this.notifyState(snapshot);
    return snapshot;
  }

  serialize(): BonusCardRewardSerializedState {
    this.assertActive();
    return Object.freeze({
      schemaVersion: 1 as const,
      setSize: this.configurationValue.setSize,
      totalCards: this.totalCardsValue,
      completedSets: this.completedSetsValue,
      variantCounts: freezeVariantCounts(this.variantCountsValue),
      denominationCounts: freezeDenominationCounts(this.denominationCountsValue),
      totalCardRewardCoins: this.totalCardRewardCoinsValue,
      totalSetCompletionRewardCoins: this.totalSetCompletionRewardCoinsValue,
      processedOutcomeIds: Object.freeze([...this.processedIds]),
      lastCollected: this.lastCollectedValue,
      eventSequence: this.eventSequenceValue,
    });
  }

  /** Replaces collection state after strict runtime validation. */
  restore(state: unknown): BonusCardCollectionSnapshot {
    this.assertActive();
    const restored = validateSerializedState(state, this.configurationValue.setSize);

    this.totalCardsValue = restored.totalCards;
    this.completedSetsValue = restored.completedSets;
    this.variantCountsValue = { ...restored.variantCounts };
    this.denominationCountsValue = { ...restored.denominationCounts };
    this.totalCardRewardCoinsValue = restored.totalCardRewardCoins;
    this.totalSetCompletionRewardCoinsValue = restored.totalSetCompletionRewardCoins;
    this.lastCollectedValue = restored.lastCollected;
    this.eventSequenceValue = restored.eventSequence;
    this.processedIds.clear();
    for (const id of restored.processedOutcomeIds) this.processedIds.add(id);
    this.lastCollectedOutcomeIdValue = restored.lastCollected?.outcomeId ?? null;
    this.lastOutcomeIdValue = restored.lastCollected?.outcomeId ?? null;
    this.restoresValue = safeIncrement(this.restoresValue, 'telemetry.restores');

    const snapshot = this.getSnapshot();
    this.notifyState(snapshot);
    return snapshot;
  }

  dispose(): void {
    if (this.disposedValue) return;
    this.disposedValue = true;
    this.eventListeners.clear();
    this.stateListeners.clear();
  }

  private collectCard(
    outcome: Extract<MedalOutcome, { itemKind: 'card' }>,
    outcomeId: string,
  ): BonusOutcomeCollected {
    const variant = getBonusCardVariant(outcome.variantId);
    if (variant.denomination !== outcome.denomination) {
      throw new Error(
        `Bonus card outcome ${outcomeId} pairs variant ${outcome.variantId} with ` +
          `denomination ${outcome.denomination}; expected ${variant.denomination}.`,
      );
    }
    const fixedStep = readNonNegativeSafeInteger(outcome.fixedStep, 'outcome.fixedStep');
    const simulatedSeconds = readNonNegativeFinite(
      outcome.simulatedSeconds,
      'outcome.simulatedSeconds',
    );

    const setProgressBefore = this.totalCardsValue % this.configurationValue.setSize;
    const nextTotalCards = safeIncrement(this.totalCardsValue, 'totalCards');
    const completesSet = nextTotalCards % this.configurationValue.setSize === 0;
    const nextCompletedSets = completesSet
      ? safeIncrement(this.completedSetsValue, 'completedSets')
      : this.completedSetsValue;
    const nextVariantCount = safeIncrement(
      this.variantCountsValue[outcome.variantId],
      `variantCounts.${outcome.variantId}`,
    );
    const nextDenominationCount = safeIncrement(
      this.denominationCountsValue[outcome.denomination],
      `denominationCounts.${outcome.denomination}`,
    );
    const cardCoins = this.configurationValue.denominationCoinAwards[outcome.denomination];
    const setCompletionCoins = completesSet
      ? this.configurationValue.setCompletionCoinAward
      : 0;
    const nextCardRewardCoins = safeAdd(
      this.totalCardRewardCoinsValue,
      cardCoins,
      'totalCardRewardCoins',
    );
    const nextSetRewardCoins = safeAdd(
      this.totalSetCompletionRewardCoinsValue,
      setCompletionCoins,
      'totalSetCompletionRewardCoins',
    );
    const cumulativeExtraCoins = safeAdd(
      nextCardRewardCoins,
      nextSetRewardCoins,
      'cumulativeExtraCoins',
    );
    const extraCoins = safeAdd(cardCoins, setCompletionCoins, 'reward.extraCoins');
    const recommendedJackpotContribution = safeAdd(
      this.configurationValue.denominationJackpotContributions[outcome.denomination],
      completesSet ? this.configurationValue.setCompletionJackpotContribution : 0,
      'reward.recommendedJackpotContribution',
    );
    const eventCount = completesSet ? 3 : 2;
    const finalSequence = safeAdd(
      this.eventSequenceValue,
      eventCount,
      'eventSequence',
    );
    const firstSequence = this.eventSequenceValue + 1;

    const lastCollected = freezeLastCollection({
      outcomeId,
      fixedStep,
      simulatedSeconds,
      variantId: outcome.variantId,
      denomination: outcome.denomination,
      collectionOrdinal: nextTotalCards,
    });

    this.processedIds.add(outcomeId);
    this.totalCardsValue = nextTotalCards;
    this.completedSetsValue = nextCompletedSets;
    this.variantCountsValue[outcome.variantId] = nextVariantCount;
    this.denominationCountsValue[outcome.denomination] = nextDenominationCount;
    this.totalCardRewardCoinsValue = nextCardRewardCoins;
    this.totalSetCompletionRewardCoinsValue = nextSetRewardCoins;
    this.lastCollectedValue = lastCollected;
    this.lastCollectedOutcomeIdValue = outcomeId;
    this.eventSequenceValue = finalSequence;
    this.cardWinsCollectedValue = safeIncrement(
      this.cardWinsCollectedValue,
      'telemetry.cardWinsCollected',
    );

    let sequence = firstSequence;
    const cardCollectedEvent: BonusCardRewardEvent = Object.freeze({
      type: 'card-collected' as const,
      sequence,
      outcomeId,
      fixedStep,
      simulatedSeconds,
      variantId: outcome.variantId,
      denomination: outcome.denomination,
      collectionOrdinal: nextTotalCards,
      variantCount: nextVariantCount,
      denominationCount: nextDenominationCount,
      setSize: this.configurationValue.setSize,
      setProgressBefore,
      setProgressAfter: nextTotalCards % this.configurationValue.setSize,
      completedSet: completesSet,
    });
    sequence += 1;

    const events: BonusCardRewardEvent[] = [cardCollectedEvent];
    if (completesSet) {
      const setCompleteEvent: BonusCardSetCompleteEvent = Object.freeze({
        type: 'set-complete',
        sequence,
        outcomeId,
        fixedStep,
        simulatedSeconds,
        setNumber: nextCompletedSets,
        setSize: this.configurationValue.setSize,
        totalCards: nextTotalCards,
        extraCoins: setCompletionCoins,
      });
      events.push(setCompleteEvent);
      sequence += 1;
    }

    const rewardEvent: BonusCardRewardAwardedEvent = Object.freeze({
      type: 'reward',
      sequence,
      outcomeId,
      fixedStep,
      simulatedSeconds,
      reason: completesSet ? 'card-and-set-completion' : 'card-collection',
      variantId: outcome.variantId,
      denomination: outcome.denomination,
      cardCoins,
      setCompletionCoins,
      extraCoins,
      cumulativeExtraCoins,
      completedSetNumber: completesSet ? nextCompletedSets : null,
      recommendedJackpotContribution,
    });
    events.push(rewardEvent);
    const immutableEvents = Object.freeze(events);

    this.recordEventTelemetry(immutableEvents);
    const snapshot = this.getSnapshot();
    for (const event of immutableEvents) this.emit(event);
    this.notifyState(snapshot);

    return Object.freeze({
      status: 'collected',
      processed: true,
      outcomeId,
      events: immutableEvents,
      snapshot,
    });
  }

  private createIgnoredResult(
    outcomeId: string,
    reason: BonusOutcomeIgnoredReason,
  ): BonusOutcomeIgnored {
    return Object.freeze({
      status: 'ignored',
      processed: true,
      reason,
      outcomeId,
      events: EMPTY_EVENTS,
      snapshot: this.getSnapshot(),
    });
  }

  private recordEventTelemetry(events: readonly BonusCardRewardEvent[]): void {
    this.eventsEmittedValue = safeAdd(
      this.eventsEmittedValue,
      events.length,
      'telemetry.eventsEmitted',
    );
    for (const event of events) {
      if (event.type === 'card-collected') {
        this.cardCollectedEventsValue = safeIncrement(
          this.cardCollectedEventsValue,
          'telemetry.cardCollectedEvents',
        );
      } else if (event.type === 'set-complete') {
        this.setCompleteEventsValue = safeIncrement(
          this.setCompleteEventsValue,
          'telemetry.setCompleteEvents',
        );
      } else {
        this.rewardEventsValue = safeIncrement(
          this.rewardEventsValue,
          'telemetry.rewardEvents',
        );
      }
    }
  }

  private emit(event: BonusCardRewardEvent): void {
    for (const listener of [...this.eventListeners]) {
      this.invokeSafely(() => listener(event));
    }
  }

  private notifyState(snapshot: BonusCardCollectionSnapshot): void {
    for (const listener of [...this.stateListeners]) {
      this.invokeSafely(() => listener(snapshot));
    }
  }

  private invokeSafely(action: () => void): void {
    try {
      action();
    } catch (error) {
      if (!this.onError) return;
      try {
        this.onError(error);
      } catch {
        // Host feedback failures must not break deterministic reward state.
      }
    }
  }

  private clearTelemetry(): void {
    this.outcomesReceivedValue = 0;
    this.cardWinsCollectedValue = 0;
    this.ignoredCoinOutcomesValue = 0;
    this.ignoredCardLossesValue = 0;
    this.duplicateOutcomeIdsRejectedValue = 0;
    this.eventsEmittedValue = 0;
    this.cardCollectedEventsValue = 0;
    this.setCompleteEventsValue = 0;
    this.rewardEventsValue = 0;
    this.resetsValue = 0;
    this.restoresValue = 0;
    this.lastOutcomeIdValue = null;
    this.eventSequenceValue = 0;
  }

  private assertActive(): void {
    if (this.disposedValue) throw new Error('BonusCardRewardSystem has been disposed.');
  }
}

export function createBonusCardRewardSystem(
  options: BonusCardRewardOptions = {},
): BonusCardRewardSystem {
  return new BonusCardRewardSystem(options);
}

function normalizeConfiguration(
  options: BonusCardRewardOptions,
): BonusCardRewardConfiguration {
  const setSize = readBoundedInteger(
    options.setSize ?? DEFAULT_BONUS_CARD_SET_SIZE,
    BONUS_CARD_REWARD_LIMITS.setSize.min,
    BONUS_CARD_REWARD_LIMITS.setSize.max,
    'setSize',
  );
  const denominationCoinAwards = normalizeDenominationValues(
    options.denominationCoinAwards,
    DEFAULT_BONUS_CARD_COIN_AWARDS,
    BONUS_CARD_REWARD_LIMITS.cardCoinAward.min,
    BONUS_CARD_REWARD_LIMITS.cardCoinAward.max,
    'denominationCoinAwards',
  );
  const denominationJackpotContributions = normalizeDenominationValues(
    options.denominationJackpotContributions,
    DEFAULT_BONUS_CARD_JACKPOT_CONTRIBUTIONS,
    BONUS_CARD_REWARD_LIMITS.jackpotContribution.min,
    BONUS_CARD_REWARD_LIMITS.jackpotContribution.max,
    'denominationJackpotContributions',
  );
  return Object.freeze({
    setSize,
    denominationCoinAwards,
    setCompletionCoinAward: readBoundedInteger(
      options.setCompletionCoinAward ?? DEFAULT_BONUS_CARD_SET_COIN_AWARD,
      BONUS_CARD_REWARD_LIMITS.setCompletionCoinAward.min,
      BONUS_CARD_REWARD_LIMITS.setCompletionCoinAward.max,
      'setCompletionCoinAward',
    ),
    denominationJackpotContributions,
    setCompletionJackpotContribution: readBoundedInteger(
      options.setCompletionJackpotContribution ??
        DEFAULT_BONUS_CARD_SET_JACKPOT_CONTRIBUTION,
      BONUS_CARD_REWARD_LIMITS.jackpotContribution.min,
      BONUS_CARD_REWARD_LIMITS.jackpotContribution.max,
      'setCompletionJackpotContribution',
    ),
  });
}

function normalizeDenominationValues(
  overrides: Partial<Record<BonusCardDenomination, number>> | undefined,
  defaults: BonusCardDenominationCounts,
  min: number,
  max: number,
  label: string,
): BonusCardDenominationCounts {
  const values = {} as Record<BonusCardDenomination, number>;
  for (const denomination of BONUS_CARD_DENOMINATIONS) {
    values[denomination] = readBoundedInteger(
      overrides?.[denomination] ?? defaults[denomination],
      min,
      max,
      `${label}.${denomination}`,
    );
  }
  return Object.freeze(values);
}

function createEmptyVariantCounts(): Record<BonusCardVariantId, number> {
  const counts = {} as Record<BonusCardVariantId, number>;
  for (const variant of BONUS_CARD_VARIANTS) counts[variant.id] = 0;
  return counts;
}

function createEmptyDenominationCounts(): Record<BonusCardDenomination, number> {
  const counts = {} as Record<BonusCardDenomination, number>;
  for (const denomination of BONUS_CARD_DENOMINATIONS) counts[denomination] = 0;
  return counts;
}

function freezeVariantCounts(
  counts: Readonly<Record<BonusCardVariantId, number>>,
): BonusCardVariantCounts {
  const copy = {} as Record<BonusCardVariantId, number>;
  for (const variant of BONUS_CARD_VARIANTS) copy[variant.id] = counts[variant.id];
  return Object.freeze(copy);
}

function freezeDenominationCounts(
  counts: Readonly<Record<BonusCardDenomination, number>>,
): BonusCardDenominationCounts {
  const copy = {} as Record<BonusCardDenomination, number>;
  for (const denomination of BONUS_CARD_DENOMINATIONS) {
    copy[denomination] = counts[denomination];
  }
  return Object.freeze(copy);
}

function freezeLastCollection(value: BonusCardLastCollection): BonusCardLastCollection {
  return Object.freeze({ ...value });
}

function validateSerializedState(
  input: unknown,
  expectedSetSize: number,
): BonusCardRewardSerializedState {
  const state = readRecord(input, 'serialized state');
  if (state.schemaVersion !== 1) {
    throw new Error('Unsupported bonus card reward state schemaVersion.');
  }
  const setSize = readNonNegativeSafeInteger(state.setSize, 'state.setSize');
  if (setSize !== expectedSetSize) {
    throw new Error(
      `Serialized setSize ${setSize} does not match configured setSize ${expectedSetSize}.`,
    );
  }

  const variantRecord = readRecord(state.variantCounts, 'state.variantCounts');
  const variantCounts = createEmptyVariantCounts();
  assertOnlyKnownKeys(
    variantRecord,
    BONUS_CARD_VARIANTS.map((variant) => variant.id),
    'state.variantCounts',
  );
  for (const variant of BONUS_CARD_VARIANTS) {
    variantCounts[variant.id] = readNonNegativeSafeInteger(
      variantRecord[variant.id],
      `state.variantCounts.${variant.id}`,
    );
  }

  const denominationRecord = readRecord(
    state.denominationCounts,
    'state.denominationCounts',
  );
  const denominationCounts = createEmptyDenominationCounts();
  assertOnlyKnownKeys(
    denominationRecord,
    BONUS_CARD_DENOMINATIONS.map(String),
    'state.denominationCounts',
  );
  for (const denomination of BONUS_CARD_DENOMINATIONS) {
    denominationCounts[denomination] = readNonNegativeSafeInteger(
      denominationRecord[String(denomination)],
      `state.denominationCounts.${denomination}`,
    );
  }

  let variantTotal = 0;
  const derivedDenominationCounts = createEmptyDenominationCounts();
  for (const variant of BONUS_CARD_VARIANTS) {
    const count = variantCounts[variant.id];
    variantTotal = safeAdd(variantTotal, count, 'state.variantCounts total');
    derivedDenominationCounts[variant.denomination] = safeAdd(
      derivedDenominationCounts[variant.denomination],
      count,
      `derivedDenominationCounts.${variant.denomination}`,
    );
  }
  const totalCards = readNonNegativeSafeInteger(state.totalCards, 'state.totalCards');
  if (variantTotal !== totalCards) {
    throw new Error('state.totalCards must equal the sum of state.variantCounts.');
  }
  for (const denomination of BONUS_CARD_DENOMINATIONS) {
    if (denominationCounts[denomination] !== derivedDenominationCounts[denomination]) {
      throw new Error(
        `state.denominationCounts.${denomination} does not match variant counts.`,
      );
    }
  }

  const completedSets = readNonNegativeSafeInteger(
    state.completedSets,
    'state.completedSets',
  );
  if (completedSets !== Math.floor(totalCards / setSize)) {
    throw new Error('state.completedSets is inconsistent with totalCards and setSize.');
  }
  const totalCardRewardCoins = readNonNegativeSafeInteger(
    state.totalCardRewardCoins,
    'state.totalCardRewardCoins',
  );
  const totalSetCompletionRewardCoins = readNonNegativeSafeInteger(
    state.totalSetCompletionRewardCoins,
    'state.totalSetCompletionRewardCoins',
  );
  safeAdd(
    totalCardRewardCoins,
    totalSetCompletionRewardCoins,
    'state.totalRewardCoins',
  );

  if (!Array.isArray(state.processedOutcomeIds)) {
    throw new TypeError('state.processedOutcomeIds must be an array.');
  }
  const processedOutcomeIds = state.processedOutcomeIds.map((id, index) =>
    readOutcomeIdWithLabel(id, `state.processedOutcomeIds[${index}]`),
  );
  if (new Set(processedOutcomeIds).size !== processedOutcomeIds.length) {
    throw new Error('state.processedOutcomeIds must not contain duplicates.');
  }
  if (processedOutcomeIds.length < totalCards) {
    throw new Error('state.processedOutcomeIds cannot contain fewer IDs than totalCards.');
  }

  const lastCollected = validateLastCollection(state.lastCollected, totalCards);
  if (lastCollected && !processedOutcomeIds.includes(lastCollected.outcomeId)) {
    throw new Error('state.lastCollected.outcomeId must be a processed outcome ID.');
  }
  const eventSequence = readNonNegativeSafeInteger(
    state.eventSequence,
    'state.eventSequence',
  );

  return Object.freeze({
    schemaVersion: 1 as const,
    setSize,
    totalCards,
    completedSets,
    variantCounts: freezeVariantCounts(variantCounts),
    denominationCounts: freezeDenominationCounts(denominationCounts),
    totalCardRewardCoins,
    totalSetCompletionRewardCoins,
    processedOutcomeIds: Object.freeze(processedOutcomeIds),
    lastCollected,
    eventSequence,
  });
}

function validateLastCollection(
  input: unknown,
  totalCards: number,
): BonusCardLastCollection | null {
  if (input === null) {
    if (totalCards !== 0) throw new Error('state.lastCollected is required when cards exist.');
    return null;
  }
  if (totalCards === 0) throw new Error('state.lastCollected must be null when no cards exist.');
  const value = readRecord(input, 'state.lastCollected');
  const outcomeId = readOutcomeIdWithLabel(
    value.outcomeId,
    'state.lastCollected.outcomeId',
  );
  const variantId = readVariantId(value.variantId, 'state.lastCollected.variantId');
  const variant = getBonusCardVariant(variantId);
  const denomination = readDenomination(
    value.denomination,
    'state.lastCollected.denomination',
  );
  if (variant.denomination !== denomination) {
    throw new Error('state.lastCollected variant and denomination do not match.');
  }
  const collectionOrdinal = readNonNegativeSafeInteger(
    value.collectionOrdinal,
    'state.lastCollected.collectionOrdinal',
  );
  if (collectionOrdinal !== totalCards) {
    throw new Error('state.lastCollected.collectionOrdinal must equal totalCards.');
  }
  return freezeLastCollection({
    outcomeId,
    fixedStep: readNonNegativeSafeInteger(
      value.fixedStep,
      'state.lastCollected.fixedStep',
    ),
    simulatedSeconds: readNonNegativeFinite(
      value.simulatedSeconds,
      'state.lastCollected.simulatedSeconds',
    ),
    variantId,
    denomination,
    collectionOrdinal,
  });
}

function readVariantId(value: unknown, label: string): BonusCardVariantId {
  if (
    typeof value !== 'string' ||
    !BONUS_CARD_VARIANTS.some((variant) => variant.id === value)
  ) {
    throw new TypeError(`${label} is not a known bonus card variant ID.`);
  }
  return value as BonusCardVariantId;
}

function readDenomination(value: unknown, label: string): BonusCardDenomination {
  if (
    typeof value !== 'number' ||
    !BONUS_CARD_DENOMINATIONS.some((denomination) => denomination === value)
  ) {
    throw new TypeError(`${label} is not a known bonus card denomination.`);
  }
  return value as BonusCardDenomination;
}

function assertOnlyKnownKeys(
  record: Readonly<Record<string, unknown>>,
  knownKeys: readonly string[],
  label: string,
): void {
  const known = new Set(knownKeys);
  for (const key of Object.keys(record)) {
    if (!known.has(key)) throw new Error(`${label} contains unknown key ${key}.`);
  }
}

function readRecord(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new TypeError(`${label} must be an object.`);
  }
  return value as Record<string, unknown>;
}

function readOutcomeId(value: string): string {
  return readOutcomeIdWithLabel(value, 'outcome.id');
}

function readOutcomeIdWithLabel(value: unknown, label: string): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${label} must be a non-empty string.`);
  }
  return value;
}

function readBoundedInteger(
  value: number,
  min: number,
  max: number,
  label: string,
): number {
  if (!Number.isSafeInteger(value) || value < min || value > max) {
    throw new RangeError(`${label} must be a safe integer from ${min} through ${max}.`);
  }
  return value;
}

function readNonNegativeSafeInteger(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`${label} must be a non-negative safe integer.`);
  }
  return value;
}

function readNonNegativeFinite(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    throw new RangeError(`${label} must be a finite non-negative number.`);
  }
  return value;
}

function safeIncrement(value: number, label: string): number {
  return safeAdd(value, 1, label);
}

function safeAdd(left: number, right: number, label: string): number {
  const result = left + right;
  if (!Number.isSafeInteger(result) || result < 0) {
    throw new RangeError(`${label} exceeded the non-negative safe integer range.`);
  }
  return result;
}
